/**
 * MosaicOralCapture.js — v6.32.164
 *
 * Browser microphone → MediaRecorder → POST audio bytes to
 * /api/mosaic/oral/transcribe/{student_id} → display transcript + the
 * auto-rated Speaking-domain MOSAIC level.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';
import MosaicBucketBadge from './MosaicBucketBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MosaicOralCapture = ({ studentId, onResult }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state === 'recording') {
      try { mediaRef.current.stop(); } catch (e) { /* noop */ }
    }
  }, []);

  const startRecording = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error('Microphone API not available in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        await uploadAndTranscribe(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      toast.error('Microphone permission denied');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    try { mediaRef.current?.stop(); } catch (e) { /* noop */ }
  };

  const uploadAndTranscribe = async (blob) => {
    if (!studentId) {
      toast.error('No student selected for oral capture');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/mosaic/oral/transcribe/${studentId}`,
        blob,
        {
          headers: { 'Content-Type': 'application/octet-stream', 'X-Audio-Ext': 'webm' },
          maxContentLength: 30 * 1024 * 1024,
          maxBodyLength: 30 * 1024 * 1024,
        },
      );
      setResult(res.data);
      toast.success(`Oral capture transcribed (${res.data.word_count} words)`);
      if (onResult) onResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transcription failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-violet-200 bg-violet-50/40 p-3" data-testid="mosaic-oral-capture">
      <div className="flex items-center gap-2 mb-2">
        <Mic className="w-4 h-4 text-violet-700" />
        <span className="text-[11px] font-bold uppercase tracking-widest text-violet-900">
          Oral Language Capture (Speaking auto-rate)
        </span>
        {result?.speaking_level ? (
          <span className="ml-auto"><MosaicBucketBadge level={result.speaking_level} compact /></span>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        {!recording ? (
          <Button
            type="button"
            onClick={startRecording}
            disabled={busy || !studentId}
            className="bg-violet-600 hover:bg-violet-700 text-white h-9"
            data-testid="oral-start-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
            {busy ? 'Transcribing…' : 'Record 20–60s prompt'}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={stopRecording}
            className="bg-rose-600 hover:bg-rose-700 text-white h-9"
            data-testid="oral-stop-btn"
          >
            <Square className="w-4 h-4 mr-2" /> Stop ({elapsed}s)
          </Button>
        )}
        <span className="text-xs text-gray-500">
          Ask the student to describe a familiar topic (school, family, weekend). Speaking level is computed from word count + lexical diversity.
        </span>
      </div>

      {result?.transcript ? (
        <div className="mt-3 p-2 bg-white border border-gray-200 text-xs text-gray-800 max-h-32 overflow-y-auto" data-testid="oral-transcript">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">
            Transcript · {result.word_count} words · unique ratio {result.unique_ratio}
          </div>
          {result.transcript || <span className="italic text-gray-400">(empty — no speech detected)</span>}
        </div>
      ) : null}

      {result?.rubric && !result.rubric.error ? (
        <div className="mt-3 p-3 bg-white border-2 border-violet-300" data-testid="oral-rubric">
          <div className="text-[10px] uppercase tracking-widest text-violet-700 mb-2 font-bold">
            MOSAIC content engine · Pedagogy Rubric
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
            {[
              { key: 'grammar',           label: 'Grammar' },
              { key: 'content_coherence', label: 'Content' },
              { key: 'register',          label: 'Register' },
              { key: 'vocabulary_depth',  label: 'Vocabulary' },
            ].map((x) => (
              <div key={x.key} className="border border-gray-200 p-2" data-testid={`oral-rubric-${x.key}`}>
                <div className="text-[10px] uppercase text-gray-500 tracking-wider">{x.label}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {result.rubric[x.key]}
                  </span>
                  <MosaicBucketBadge level={result.rubric[x.key]} compact />
                </div>
              </div>
            ))}
          </div>
          {result.rubric.teacher_note ? (
            <div className="text-sm text-gray-800 italic border-l-4 border-violet-400 pl-2" data-testid="oral-rubric-note">
              "{result.rubric.teacher_note}"
            </div>
          ) : null}
        </div>
      ) : null}
      {result?.rubric?.error ? (
        <div className="mt-2 text-[11px] text-amber-700" data-testid="oral-rubric-error">
          Rubric pass skipped: {result.rubric.error}
        </div>
      ) : null}
    </div>
  );
};

export default MosaicOralCapture;
